import { flashBagError, flashBagSuccess } from '@tools/flashbag';
import { Logger } from '@tools/logger/logger';
import { createApp } from 'vue';
import { eventBus } from '@tools/event-bus';
import { Requests } from '@tools/requests';

const DEFAULT_DURATION = 4000;

export const flashbags = () => {
  const component = {
    data() {
      return {
        flashbags: [],
        duration: DEFAULT_DURATION,
        sendEmailValidationLoading: false,
      };
    },
    mounted() {
      this.loadFlashbags();

      this.flashbagListener = (event) => {
        const flashbag = event.detail;

        // Empty the flashbags
        this.flashbags = [];
        this.$nextTick(() => {
          this.flashbags = [ ...this.flashbags, flashbag ];
          this.duration = flashbag.duration ?? DEFAULT_DURATION;
        });
      };

      eventBus.addEventListener('flashbags-push', this.flashbagListener);
    },
    beforeUnmount() {
      eventBus.removeEventListener('flashbags-push', this.flashbagListener);
    },
    watch: {
      flashbags() {
        this.attachClickEventSendValidationEmail();
      },
    },
    methods: {
      sendEmailValidation(event) {
        event.preventDefault();
        if (!this.sendEmailValidationLoading) {
          this.sendEmailValidationLoading = true;
          const sendEmailLink = `${event.target.href}?json=true`;
          Requests.get(sendEmailLink)
            .then((response) => {
              const { message } = response.data;
              flashBagSuccess(message);
            })
            .catch((error) => {
              flashBagError();
              Logger.error('Error occurred', error);
            })
            .finally(() => {
              this.sendEmailValidationLoading = false;
            });
        }
      },
      attachClickEventSendValidationEmail() {
        let i = 0;
        const e = setInterval(() => {
          i++;
          const link = document.querySelector('.sendEmailValidation');
          if (link || i >= 3) {
            clearInterval(e);
            if (link) {
              link.addEventListener('click', (event) => {
                this.sendEmailValidation(event);
              });
            }
          }
        }, 100);
      },
      /**
       * loadFlashbags
       * @description Load flashbags
       */
      loadFlashbags() {
        const flashbagContentElement = document.getElementById('flashbags-content');
        if (flashbagContentElement) {
          const rawFlashbags = JSON.parse(flashbagContentElement.dataset.initialstate);
          const formattedFlashbags = [];

          Object.entries(rawFlashbags).forEach(([ type, messages ]) => {
            messages.forEach((message) => {
              formattedFlashbags.push({ type, message });
            });
          });

          this.flashbags = formattedFlashbags;
        }
      },
      /**
       * closeFlashbag
       * @description Close a flashbag
       */
      closeFlashbag(event) {
        const { target } = event;
        const parent = this.findParentWithClass(target, 'flashbags__alert');

        if (parent) {
          this.slideUp(parent, 0);
        }
      },
      /**
       * enter
       * @description Animate flashbags when added the to the DOM
       */
      enter(el, done) {
        el.style.display = 'none'; // eslint-disable-line no-param-reassign
        el.style.overflow = 'hidden'; // eslint-disable-line no-param-reassign
        // Show them
        this.slideDown(el, done, 500);
        // Hide them after 4sec
        setTimeout(() => {
          this.slideUp(el, 350);
        }, this.duration);
      },
      findParentWithClass(element, className) {
        while (element && !element.classList.contains(className)) {
          element = element.parentElement; // eslint-disable-line no-param-reassign
        }
        return element;
      },
      slideUp(element, duration = 500) {
        element.style.transition = `height ${duration}ms ease`; // eslint-disable-line no-param-reassign
        element.style.height = '0'; // eslint-disable-line no-param-reassign

        setTimeout(() => {
          element.style.display = 'none'; // eslint-disable-line no-param-reassign
        }, duration);
      },
      slideDown(element, done, duration = 500) {
        element.style.display = 'block'; // eslint-disable-line no-param-reassign

        const startHeight = 0;
        const endHeight = element.scrollHeight;

        const animation = element.animate(
          [
            {
              height: `${startHeight}px`,
              overflow: 'hidden',
            },
            {
              height: `${endHeight}px`,
              overflow: 'hidden',
            },
          ],
          {
            duration,
            easing: 'ease-out',
            fill: 'both',
          },
        );

        animation.onfinish = function () {
          if (typeof done === 'function') {
            done();
          }
        };
      },
    },
    template:
      `
        <div
          id="flashbags"
          class="flashbags"
        >
          <TransitionGroup
            tag="div"
            @enter="enter"
          >
            <div
              v-for="flashbag in flashbags"
              :key="'message' + flashbag.type"
              :class="flashbag.type"
            >
              <div
                class="flashbags__alert"
                :class="[ 'flashbags__alert--' + flashbag.type ]"
              >
                <span class="icon" />

                <p v-html="flashbag.message" />

                <div class="btn-close" @click="closeFlashbag" />
              </div>
            </div>
          </TransitionGroup>
        </div>
      `,
  };

  return createApp(component).mount('#flashbags');
};
